import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { NOT_STARTED, IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_DOCUMENT_TEMPLATES } from '@/lib/constants/api'
import getDocumentTemplateModel from './models/documentTemplate'
import updateStatus from '@/store/helpers/updateStatus'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'

const documentTemplate = {
  namespaced: true,
  state: {
    ...getDocumentTemplateModel()
  },
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    clearDocumentTemplate: (state) => {
      const model = getDocumentTemplateModel()
      Object.keys(model).forEach(key => { state[key] = model[key] })
    },
    setDocumentTemplate: (state, template) => {
      state.id = template.id
      state.name = template.name
      state.documentTypeId = template.documentTypeId
      state.documentTypeName = template.documentTypeName
      state.clientId = template.clientId
      state.clientName = template.clientName
      state.htmlBody = template.htmlBody
      state.isActive = template.isActive
      state.createdBy = template.createdBy
      state.updatedBy = template.updatedBy
      state.createdAt = template.createdAt
      state.updatedAt = template.updatedAt
      state.deactivatedByUserId = template.deactivatedByUserId
      state.deactivatedAt = template.deactivatedAt
    }
  },
  actions: {
    save ({ state }) {
      const { name, documentTypeId, clientId, htmlBody, isActive } = state
      return api.doPost({
        path: PATH_DOCUMENT_TEMPLATES,
        data: { name, documentTypeId, clientId, htmlBody, isActive }
      })
    },
    update ({ state }) {
      const { id, name, documentTypeId, clientId, htmlBody, isActive } = state
      return api.doPut({
        path: PATH_DOCUMENT_TEMPLATES,
        data: { id, name, documentTypeId, clientId, htmlBody, isActive }
      })
    },
    getDocumentTemplate: ({ commit, dispatch }, { id }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Loading template, please wait...' })

      return api.doGet({ path: `${PATH_DOCUMENT_TEMPLATES}/${id}` })
        .then(json => json.data)
        .then(template => {
          commit('setDocumentTemplate', template)
          commit('updateStatus', { status: COMPLETE })
          return template
        }).catch((error) => {
          commit('updateStatus', { status: ERROR, error })
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    saveDocumentTemplate: ({ state, commit, dispatch }) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving template, please wait...' })

      const actionName = state.id ? 'update' : 'save'

      return dispatch(actionName).then(response => {
        if (!response.data) {
          if (response.errorMessage) {
            throw new Error(response.errorMessage)
          }
          throw new Error('Unable to save template. Check required fields.')
        }

        commit('setDocumentTemplate', response.data)
        commit('updateStatus', { status: COMPLETE })
        notifySuccess(dispatch, 'Template saved')

        return response
      }).catch((error) => {
        commit('updateStatus', { status: ERROR, error })
        notifyError(dispatch, error || 'Unable to save template. Try again later')
        throw error
      })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    copyDocumentTemplate: ({ dispatch }, { sourceTemplateId, name, clientId }) => {
      showProgressModal({ dispatch, message: 'Copying template, please wait...' })
      return api.doPost({
        path: `${PATH_DOCUMENT_TEMPLATES}/${sourceTemplateId}/copy`,
        data: { name, clientId }
      })
        .then(response => {
          if (!response.data) {
            throw new Error('Unable to create document template for this client.')
          }
          return response.data
        })
        .catch(error => {
          notifyError(dispatch, error || 'Unable to create document template')
          throw error
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    clearDocumentTemplate: ({ commit }) => {
      commit('clearDocumentTemplate')
      return commit('updateStatus', { status: NOT_STARTED })
    }
  }
}

export default documentTemplate
